/**
 * 捏脸类目清单（单一事实源）——开局建角页与「造型工作室」共用同一份表。
 *
 * 立档理由：这两处都要渲「类目页签 + 部件条 + 鼻嘴 Y 微调」，清单与联动置灰判据若各写一份，
 * 以后加件/换类目就会只改一处（头像系统已有「置灰判据两份镜像」的债，⛔ 再加第三份）。
 *
 * `studioOnly` 的类目＝不进开局建角页、只在造型间列，现有三项、同一种理由（开局锁定＝服装/眼镜/配饰：
 * 用户 2026-10-05 令「统一穿白T不能改，衣服和饰品改成后面在游戏里获得，在造型间可以换」），
 * 2026-10-20 用户令又点名「开局的形象创建没有饰品和眼镜，没有衣服」，所以这三格由
 * `CharacterCreatePanel.START_LOCKED_SLOTS` 钉死，不许随「随机造型」跳动；眼睛同日令「加回去」＝撤出 studioOnly。
 * 胡须类目 2026-10-20 用户令整枚删除（造型间不列）：BEARDS 部件数组保留（男 NPC 中老年随机渲染仍在用，
 * 且数组下标制不许动），只是不再有玩家可选的页签。岁月痕迹同理不入表（2026-10-07 令：只随年龄渲染通道使用）。
 * ⚠️ 加件只能往各 parts 数组末尾追加（存档按下标记，插入/删除/调序＝全体换脸），见 docs/头像部件系统.md §五。
 */
import { AvatarConfig, LOOSE_BACK_OK } from './config';
import {
    ACCESSORIES, BROWS, CLOTHES, EYES, FACES, GLASSES,
    HAIR_BACKS, HAIR_FRONTS, HAIR_SIDES, MOUTHS, NOSES, Part,
} from './parts';
import { EYE_COLORS, HAIR_COLORS, SKINS, Tone } from './palettes';

export type PaletteKind = 'skin' | 'hairColor' | 'eyeColor';

export interface FaceCategory {
    key: keyof AvatarConfig;
    label: string;
    parts?: Part[];
    palette?: PaletteKind;
    /** 五官 Y 微调槽（鼻/嘴，−6~+6 档，同编辑器） */
    nudge?: 'noseDy' | 'mouthDy';
    /** 不进开局建角页；造型间照列。现有三项＝服装/眼镜/配饰（开局锁定、往后在造型间换，
     *  由 `CharacterCreatePanel` 钉死值，见 `START_LOCKED_SLOTS`）。 */
    studioOnly?: boolean;
}

export const FACE_CATEGORIES: FaceCategory[] = [
    { key: 'face', label: '脸型', parts: FACES },
    { key: 'skin', label: '肤色', palette: 'skin' },
    { key: 'hairBack', label: '后发', parts: HAIR_BACKS },
    { key: 'hairSides', label: '侧发', parts: HAIR_SIDES },
    { key: 'hairFront', label: '刘海', parts: HAIR_FRONTS },
    { key: 'hairColor', label: '发色', palette: 'hairColor' },
    { key: 'brow', label: '眉毛', parts: BROWS },
    { key: 'eye', label: '眼睛', parts: EYES },
    { key: 'eyeColor', label: '瞳色', palette: 'eyeColor' },
    { key: 'nose', label: '鼻子', parts: NOSES, nudge: 'noseDy' },
    { key: 'mouth', label: '嘴巴', parts: MOUTHS, nudge: 'mouthDy' },
    { key: 'cloth', label: '服装', parts: CLOTHES, studioOnly: true },
    { key: 'glasses', label: '眼镜', parts: GLASSES, studioOnly: true },
    { key: 'acc', label: '配饰', parts: ACCESSORIES, studioOnly: true },
];

/** 开局建角页用的子集（与清单入档前逐字等价：⛔ 因工作室扩表而多出新玩家页签） */
export const CREATE_PAGE_CATEGORIES: FaceCategory[] = FACE_CATEGORIES.filter(c => !c.studioOnly);

export const PALETTE_TONES: Record<PaletteKind, Tone[] | string[]> = {
    skin: SKINS,
    hairColor: HAIR_COLORS,
    eyeColor: EYE_COLORS,
};

/** 联动置灰：刘海=无 ⇒ 侧发只留「无刘海」；侧发=披发 ⇒ 后发只留白名单（判据与 config.applyFrontSideRule 同源） */
export function isOptionDisabled(cfg: AvatarConfig, cat: FaceCategory, part: Part): boolean {
    if (cat.key === 'hairSides') {
        return cfg.hairFront < HAIR_FRONTS.length
            && HAIR_FRONTS[cfg.hairFront]?.id === 'none'
            && part.id !== 'sSideNone';
    }
    if (cat.key === 'hairBack') {
        return HAIR_SIDES[cfg.hairSides]?.id === 'sSideLoose' && !LOOSE_BACK_OK.includes(part.id);
    }
    return false;
}

/**
 * 点选后只就地挪高亮/置灰，⛔ 重建整条。
 * 立档理由（A 档）：条内每张缩略图 = 基准脸把**本类目这一槽**换成该件，而一次点选改的正是这一槽
 * ⇒ 那几张图画的东西一个字没变，重建＝白重出 N 张整脸小图（成本见 `thumbs.ts`）。
 * 色板类目（圆点）与此无关，调用方自己按 `cat.parts` 分流。
 */
export function syncStripState(strip: HTMLElement, cfg: AvatarConfig, cat: FaceCategory): void {
    if (!cat.parts) return;
    strip.querySelectorAll<HTMLElement>('.cc-opt[data-i]').forEach(opt => {
        const i = Number(opt.dataset.i);
        const part = cat.parts[i];
        if (!part) return;
        const disabled = isOptionDisabled(cfg, cat, part);
        opt.classList.toggle('disabled', disabled);
        opt.classList.toggle('selected', cfg[cat.key] === i && !disabled);
    });
}

/** 一次点选连带改了几个槽（正常 1 个；`applyFrontSideRule` 顶到第二个槽时才需要重画整条） */
export function changedSlotCount(a: AvatarConfig, b: AvatarConfig): number {
    let n = 0;
    (Object.keys(b) as (keyof AvatarConfig)[]).forEach(k => { if (a[k] !== b[k]) n++; });
    return n;
}
